import * as React from 'react';
import { useTranslation } from 'next-i18next';
import { ArrowPathIcon, PencilSquareIcon } from '@heroicons/react/24/outline';

import { cn } from '@restworld/utils-common';
import { Button as ButtonV2 } from '@restworld/ui-ds-base';

// Same layout as EstablishmentNotHORECA, the other screen that ends a venue lookup.
const Container = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('w-full h-full inline-flex flex-col justify-center items-center gap-10', className)} {...props} />
);
const Section = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('self-stretch flex flex-col justify-start items-start gap-4', className)} {...props} />
);
const Title = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <h5
    className={cn('self-stretch text-center justify-start text-neutral-700 text-xl font-semibold', className)}
    {...props}
  />
);
const Subtitle = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <p
    className={cn('self-stretch text-center justify-start text-neutral-200 text-base font-medium', className)}
    {...props}
  />
);

interface VenueLookupErrorProps {
  venueName?: string;
  onRetry: () => void;
  onAddManually: () => void;
}

/**
 * Shown in place of the AI loader when the venue lookup fails for good (network error, 401, 5xx).
 * The employer picked this venue themselves, so the alternative to retrying is entering it by
 * hand, not searching again. The onboarding renders inside a <form>, so both buttons are
 * `type="button"`: a default submit would reload /getting-started and drop the step.
 */
const VenueLookupError: React.FC<VenueLookupErrorProps> = ({ venueName, onRetry, onAddManually }) => {
  const { t } = useTranslation('restaurant-onboarding');
  return (
    <Container role="alert">
      <Section>
        <Title>{t('venue_lookup_error.title')}</Title>
        {venueName && <Subtitle>{venueName}</Subtitle>}
        <Subtitle>{t('venue_lookup_error.subtitle')}</Subtitle>
      </Section>
      <Section>
        <ButtonV2
          type="button"
          EndIcon={ArrowPathIcon}
          variant="primary"
          className="w-full"
          size="md"
          onClick={onRetry}
        >
          {t('venue_lookup_error.retry')}
        </ButtonV2>
        <ButtonV2
          type="button"
          EndIcon={PencilSquareIcon}
          variant="secondary-restworld"
          className="w-full"
          size="md"
          onClick={onAddManually}
        >
          {/* Same label as the manual-entry link under the Google suggestions. */}
          {t('click_here_to_enter_manually')}
        </ButtonV2>
      </Section>
    </Container>
  );
};

export default VenueLookupError;
